/**
 * apps/web :: hooks :: useUserProfile
 *
 * Tiny shared hook that resolves the authenticated user's stored
 * `UserProfile` so feature surfaces (RuleDiffForm — ROADS-372, future
 * personalised pre-fills) can derive their defaults from real profile
 * data instead of hard-coding "India / California / Telugu".
 *
 * Caching policy mirrors {@link useFirstPassUnlocked}:
 *   - One module-level promise; subsequent mounts await the same one.
 *   - On error the cache is dropped so a later mount can retry.
 *   - {@link resetUserProfileCache} is the test escape hatch.
 *
 * Why not React Query: same rationale as `useFirstPassUnlocked` — the
 * learner shell already builds on hand-rolled `useEffect` for auth +
 * profile reads, and a one-shape singleton hook keeps the change
 * surface narrow. A future ticket may consolidate auth/profile/exam
 * onto TanStack Query in one shot.
 *
 * The hook is callable from ANY component, including ones that may
 * mount under an anonymous AuthProvider. It blindly fetches
 * `/api/profile`; on 401 the underlying `apiFetch` throws an `ApiError`
 * which we surface as `error` (callers that want anonymous-safe
 * behaviour should gate the call on `useAuth().state.kind === "authenticated"`).
 */
import { useEffect, useState } from "react";

import { ApiError } from "@/lib/api";
import { getProfile, type UserProfile } from "@/lib/profile-api";

export interface UserProfileState {
  /** True while the first request is in flight. */
  readonly loading: boolean;
  /** The fetched profile, or `null` if the user has no profile (404). */
  readonly profile: UserProfile | null;
  /** Non-null on non-2xx, non-404 errors. Callers can surface a banner. */
  readonly error: Error | null;
}

let cachedPromise: Promise<UserProfile | null> | null = null;

/** Test/escape hatch: clear the module-level cache. */
export function resetUserProfileCache(): void {
  cachedPromise = null;
}

async function resolveOnce(): Promise<UserProfile | null> {
  return getProfile();
}

export default function useUserProfile(): UserProfileState {
  const [state, setState] = useState<UserProfileState>({
    loading: true,
    profile: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    if (cachedPromise == null) {
      cachedPromise = resolveOnce().catch((err) => {
        // Drop the cache on error so a later mount can retry.
        cachedPromise = null;
        throw err;
      });
    }
    cachedPromise
      .then((profile) => {
        if (!cancelled) setState({ loading: false, profile, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error =
          err instanceof Error
            ? err
            : err instanceof ApiError
            ? new Error(err.message)
            : new Error("profile fetch failed");
        setState({ loading: false, profile: null, error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
